/**
 * Platform — cross-tenant admin (Sprint BJ).
 *
 * Only renders for users with the `admin.tenants` scope; the
 * sidebar entry is hidden otherwise (App.tsx-level gate).
 * The page is two cards: a platform rollup hero with MTD numbers,
 * and a tenants table with inline suspend/unsuspend.
 *
 * Stays read-mostly on purpose. Hard delete + impersonation are
 * deliberately not exposed (see api/platform.py docstring) —
 * those are footguns even for the platform owner.
 */

import { useCallback, useState } from "react";
import {
  AlertOctagon, Building2, CheckCircle2, ChevronDown, ChevronRight,
  Coins, Pause, Play, RefreshCw, Users,
} from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";

import {
  platformApi,
  type PlatformTenantRow,
  type PlatformUsageRollup,
  type TenantHealthDto,
} from "../shared/api/platform";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock } from "../shared/components/States";
import { SkeletonTable, SkeletonTiles } from "../shared/components/Skeleton";


export function PlatformPage(): JSX.Element {
  const t = useT();
  const rollupFetcher = useCallback(
    (signal: AbortSignal) => platformApi.usageRollup({ signal }),
    [],
  );
  const tenantsFetcher = useCallback(
    (signal: AbortSignal) => platformApi.listTenants({ signal }),
    [],
  );

  const { data: rollup,  error: rErr, loading: rLoading, refetch: refetchR } =
    useFetch<PlatformUsageRollup>(rollupFetcher, []);
  const { data: tenants, error: tErr, loading: tLoading, refetch: refetchT } =
    useFetch<PlatformTenantRow[]>(tenantsFetcher, []);

  const refresh = () => { refetchR(); refetchT(); };

  return (
    <div className="flex flex-col gap-4">
      {/* ----- Hero rollup --------------------------------------------- */}
      <Card>
        <CardHeader
          title={t("platform.page.title")}
          description={t("platform.page.subtitle")}
          actions={
            <Button variant="ghost" size="xs"
                    leftIcon={<RefreshCw className="size-3.5" />}
                    onClick={refresh}>
              {t("common.refresh")}
            </Button>
          }
        />
        {rLoading && !rollup && <SkeletonTiles count={4} />}
        {rErr && <ErrorBlock error={rErr} onRetry={refetchR} />}
        {rollup && (
          <div className="grid grid-cols-12 gap-3">
            <Hero
              icon={<Building2 className="size-4 text-base-500" />}
              label={t("platform.tile.tenants")}
              primary={rollup.tenant_count.toString()}
              secondary={`${rollup.active_count} ${t("platform.tile.tenants.hint")}`}
            />
            <Hero
              icon={<Users className="size-4 text-base-500" />}
              label={t("platform.tile.users")}
              primary={rollup.total_users.toString()}
              secondary={t("platform.tile.users.hint")}
            />
            <Hero
              icon={<Coins className="size-4 text-ai-600" />}
              label={t("platform.tile.spent")}
              primary={`$${rollup.current_month_usd.toFixed(2)}`}
              secondary={t("platform.tile.spent.hint")}
            />
            <Hero
              icon={<CheckCircle2 className="size-4 text-base-500" />}
              label={t("platform.tile.period")}
              primary={new Date(rollup.period_start).toLocaleDateString()}
              secondary={new Date(rollup.period_end).toLocaleDateString()}
            />
          </div>
        )}
      </Card>

      {/* ----- Tenants table ------------------------------------------- */}
      <Card>
        <CardHeader
          title={t("platform.tenants.title")}
          description={tenants ? String(tenants.length) : ""}
        />
        {tLoading && !tenants && <SkeletonTable rows={6} cols={8} />}
        {tErr && <ErrorBlock error={tErr} onRetry={refetchT} />}
        {tenants && <TenantsTable rows={tenants} onChanged={refetchT} />}
      </Card>
    </div>
  );
}


function Hero(props: {
  icon: JSX.Element; label: string; primary: string; secondary: string;
}): JSX.Element {
  return (
    <div className="col-span-3 rounded-sm border border-base-200 bg-surface px-3 py-2">
      <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide text-base-500">
        {props.icon}{props.label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-base-900">
        {props.primary}
      </div>
      <div className="text-2xs text-base-500">{props.secondary}</div>
    </div>
  );
}


function TenantsTable(props: {
  rows:      PlatformTenantRow[];
  onChanged: () => void;
}): JSX.Element {
  const t = useT();
  if (props.rows.length === 0) {
    return <div className="py-6 text-center text-sm text-base-500">{t("platform.tenants.empty")}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-2xs uppercase tracking-wide text-base-500">
          <tr className="border-b border-base-200">
            <th className="px-2 py-1" aria-hidden="true" />
            <th className="px-2 py-1 text-left">{t("platform.col.slug")}</th>
            <th className="px-2 py-1 text-left">{t("platform.col.name")}</th>
            <th className="px-2 py-1 text-left">{t("platform.col.status")}</th>
            <th className="px-2 py-1 text-right">{t("platform.col.users")}</th>
            <th className="px-2 py-1 text-right">{t("platform.col.spend")}</th>
            <th className="px-2 py-1 text-right">{t("platform.col.hard_cap")}</th>
            <th className="px-2 py-1 text-left">{t("platform.col.created")}</th>
            <th className="px-2 py-1 text-right" />
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r) => (
            <TenantRow key={r.id} row={r} onChanged={props.onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function TenantRow(props: {
  row:       PlatformTenantRow;
  onChanged: () => void;
}): JSX.Element {
  const t = useT();
  const { row } = props;
  const [busy, setBusy] = useState(false);

  // Sprint I1 — expand-to-show-health affordance. Lazy fetch:
  // the GET /platform/tenants/{id}/health round-trip only fires
  // the first time the row opens. Subsequent toggles re-render
  // the cached payload. Refresh-on-demand via the chevron's
  // double-click is intentionally absent — operators who want
  // a fresh number use the page-level Refresh.
  const [open,   setOpen]   = useState(false);
  const [health, setHealth] = useState<TenantHealthDto | null>(null);
  const [hErr,   setHErr]   = useState<Error | null>(null);
  const [hBusy,  setHBusy]  = useState(false);

  const toggleOpen = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && health === null && !hBusy) {
      setHBusy(true);
      setHErr(null);
      try {
        const h = await platformApi.tenantHealth(row.id);
        setHealth(h);
      } catch (e) {
        setHErr(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setHBusy(false);
      }
    }
  };

  const toggle = async () => {
    const next = row.status === "active" ? "suspended" : "active";
    setBusy(true);
    try {
      await platformApi.updateTenantStatus(row.id, next);
      props.onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="border-b border-base-100 hover:bg-base-50">
        <td className="px-2 py-1">
          <button
            type="button"
            onClick={toggleOpen}
            aria-label={t("platform.row.toggle")}
            className="grid size-5 place-items-center rounded-sm text-base-500 hover:bg-base-100 hover:text-base-800"
          >
            {open
              ? <ChevronDown className="size-3.5" aria-hidden="true" />
              : <ChevronRight className="size-3.5" aria-hidden="true" />}
          </button>
        </td>
        <td className="px-2 py-1 font-mono text-base-800">{row.slug}</td>
        <td className="px-2 py-1 text-base-800">{row.name}</td>
        <td className="px-2 py-1">
          <StatusBadge status={row.status} />
        </td>
        <td className="px-2 py-1 text-right tabular-nums">{row.user_count}</td>
        <td className="px-2 py-1 text-right tabular-nums">
          ${row.current_month_usd.toFixed(2)}
        </td>
        <td className="px-2 py-1 text-right tabular-nums text-base-600">
          {row.monthly_hard_usd == null ? "—" : `$${row.monthly_hard_usd.toFixed(2)}`}
        </td>
        <td className="px-2 py-1 text-base-600">
          {new Date(row.created_at).toLocaleDateString()}
        </td>
        <td className="px-2 py-1 text-right">
          <Button
            variant="ghost" size="xs"
            disabled={busy}
            leftIcon={row.status === "active"
              ? <Pause className="size-3" />
              : <Play  className="size-3" />}
            onClick={toggle}
          >
            {row.status === "active" ? t("platform.action.suspend") : t("platform.action.activate")}
          </Button>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-base-100 bg-base-50">
          <td colSpan={9} className="px-3 py-2">
            {hBusy && (
              <div className="text-2xs text-base-500">{t("common.loading")}</div>
            )}
            {hErr && (
              <div className="text-2xs text-danger-700">{hErr.message}</div>
            )}
            {health && <TenantHealthBlock health={health} />}
          </td>
        </tr>
      )}
    </>
  );
}


function StatusBadge({ status }: { status: string }): JSX.Element {
  if (status === "active")    return <Badge tone="ai" dot>active</Badge>;
  if (status === "suspended") return <Badge tone="danger" dot>
    <AlertOctagon className="mr-1 size-3" />suspended
  </Badge>;
  return <Badge tone="neutral" dot>{status}</Badge>;
}


/**
 * Sprint I1 — expanded health rollup rendered under a tenant
 * row when the chevron is opened. Five facts in a tight grid:
 *
 *   1. Activity   — last_login_at, last_conversation_at
 *   2. Spend      — current vs. projected month-end
 *   3. Connectors — installed / healthy ratio
 *   4. Locale     — informational; tells the operator which
 *                   language the tenant's emails ship in
 *   5. Overall    — a top-line green/amber/red pill mirroring
 *                   the server-computed `is_healthy` boolean
 *
 * "Never" is rendered when last_login_at is null — distinct
 * from "loading" so an empty tenant doesn't look in-flight.
 */
function TenantHealthBlock({ health }: { health: TenantHealthDto }): JSX.Element {
  const t = useT();

  const fmt = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString() : t("platform.health.never");

  const overallTone = health.is_healthy
    ? "success"
    : health.projected_over_hard
    ? "danger"
    : "heritage";

  const overallLabel = health.is_healthy
    ? t("platform.health.overall.healthy")
    : t("platform.health.overall.needs_attention");

  return (
    <div className="grid grid-cols-12 gap-3 text-2xs">
      <HealthFact
        label={t("platform.health.last_login")}
        value={fmt(health.last_login_at)}
        span={3}
      />
      <HealthFact
        label={t("platform.health.last_conversation")}
        value={fmt(health.last_conversation_at)}
        span={3}
      />
      <HealthFact
        label={t("platform.health.spend")}
        value={`$${health.current_month_usd.toFixed(2)}`}
        sub={t("platform.health.projected", {
          projected: health.projected_month_usd.toFixed(2),
        })}
        tone={health.projected_over_hard ? "danger" : "default"}
        span={3}
      />
      <HealthFact
        label={t("platform.health.connectors")}
        value={`${health.connectors_healthy} / ${health.connectors_installed}`}
        sub={t("platform.health.locale_hint", { locale: health.locale })}
        tone={
          health.connectors_installed > 0 &&
          health.connectors_healthy < health.connectors_installed
            ? "warn"
            : "default"
        }
        span={3}
      />
      <div className="col-span-12 pt-1">
        <Badge tone={overallTone} dot>{overallLabel}</Badge>
      </div>
    </div>
  );
}


// Tailwind's JIT scans source for class strings literally; a
// `col-span-${dynamic}` template won't be picked up. Static
// lookup keeps the four span values we actually use safe.
const SPAN_CLASS: Record<number, string> = {
  3: "col-span-3",
  4: "col-span-4",
  6: "col-span-6",
  12: "col-span-12",
};


function HealthFact(props: {
  label: string;
  value: string;
  sub?:  string;
  tone?: "default" | "warn" | "danger";
  span:  3 | 4 | 6 | 12;
}): JSX.Element {
  const valueTone =
    props.tone === "danger" ? "text-danger-700" :
    props.tone === "warn"   ? "text-warning-700" :
                              "text-base-900";
  return (
    <div className={`${SPAN_CLASS[props.span]} rounded-sm border border-base-200 bg-surface px-2 py-1.5`}>
      <div className="text-2xs font-medium uppercase tracking-wide text-base-500">
        {props.label}
      </div>
      <div className={`mt-0.5 font-mono tabular-nums ${valueTone}`}>
        {props.value}
      </div>
      {props.sub && (
        <div className="text-2xs text-base-500">{props.sub}</div>
      )}
    </div>
  );
}
